"use client";

import React, { createContext, useState, useContext, useEffect } from "react";
import { toast } from "react-toastify";
import { BASE_URL } from "../config/apiConfig";

export const ComplaintTypeContext = createContext({
  complaintTypes: [],
});

export const ComplaintTypeProvider = ({ children }) => {
  const [complaintTypes, setComplaintTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // ── Token helper ─────────────────────────────
  const getToken = () => localStorage.getItem("token");

  // ✅ Safe JSON parser
  const parseJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return null;
    }
  };

  // ─────────────────────────────────────────────
  // GET ALL COMPLAINT TYPES
  // ─────────────────────────────────────────────
  const fetchComplaintTypes = async () => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/complaint-types`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        setError("Failed to fetch complaint types");
        return;
      }

      const data = await parseJson(res);

      if (!data) {
        setError("Invalid server response");
        return;
      }

      if (data.status === 200) {
        setComplaintTypes(data.data || []);
      } else {
        setError(data.message || "Failed to fetch complaint types");
      }
    } catch (err) {
      console.error(err);
      setError("Error fetching complaint types");
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // CREATE COMPLAINT TYPE
  // ─────────────────────────────────────────────
  const createComplaintType = async (payload) => {
    setLoading(true);
    setError("");

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/complaint-types`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to create complaint type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchComplaintTypes();

        toast.success(data.message || "Complaint type created successfully");

        return {
          success: true,
          message: data.message || "Complaint type created successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to create complaint type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error creating complaint type",
      };
    } finally {
      setLoading(false);
    }
  };

  // ─────────────────────────────────────────────
  // UPDATE COMPLAINT TYPE
  // ─────────────────────────────────────────────
  const updateComplaintType = async (id, payload) => {
    setLoading(true);

    try {
      const token = getToken();

      const res = await fetch(`${BASE_URL}/complaint-types/${id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const data = await parseJson(res);
        return {
          success: false,
          message: data?.message || "Failed to update complaint type",
        };
      }

      const data = await parseJson(res);

      if (!data) {
        return {
          success: false,
          message: "Invalid server response",
        };
      }

      if (data.status === 200) {
        await fetchComplaintTypes();

        toast.success(data.message || "Complaint type updated successfully");

        return {
          success: true,
          message: data.message || "Complaint type updated successfully",
          data,
        };
      }

      return {
        success: false,
        message: data.message || "Failed to update complaint type",
      };
    } catch (err) {
      console.error(err);

      return {
        success: false,
        message: "Error updating complaint type",
      };
    } finally {
      setLoading(false);
    }
  };

  const updateComplaintTypeStatus = async (id, payload) => {
  setLoading(true);

  try {
    const token = getToken();

    const res = await fetch(
      `${BASE_URL}/complaint-types/${id}/status`,
      {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(payload),
      }
    );

    const data = await parseJson(res);

    if (data?.status === 200) {
      await fetchComplaintTypes();

      toast.success(
        data.message || "Status updated successfully"
      );

      return {
        success: true,
        data,
      };
    }

    return {
      success: false,
      message: data?.message || "Status update failed",
    };

  } catch (error) {
    console.error(error);

    return {
      success: false,
      message: "Error updating status",
    };

  } finally {
    setLoading(false);
  }
};

  // ─────────────────────────────────────────────
  // DELETE COMPLAINT TYPE
  // ─────────────────────────────────────────────
  const deleteComplaintType = async (id, payload) => {
  setLoading(true);

  try {
    const token = getToken();

    const res = await fetch(`${BASE_URL}/complaint-types/${id}`, {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const data = await parseJson(res);

      return {
        success: false,
        message: data?.message || "Failed to delete complaint type",
      };
    }

    const data = await parseJson(res);

    if (!data) {
      return {
        success: false,
        message: "Invalid server response",
      };
    }

    if (data.status === 200) {
      await fetchComplaintTypes();

      toast.success(data.message || "Complaint type deleted successfully");

      return {
        success: true,
        message: data.message || "Complaint type deleted successfully",
        data,
      };
    }

    return {
      success: false,
      message: data.message || "Failed to delete complaint type",
    };

  } catch (err) {
    console.error(err);

    return {
      success: false,
      message: "Error deleting complaint type",
    };

  } finally {
    setLoading(false);
  }
};

  // ─────────────────────────────────────────────
  // ACTIVE ONLY FILTER (helper)
  // ─────────────────────────────────────────────
  const activeComplaintTypes = complaintTypes.filter(
    (c) => c.active === true || c.status === "active"
  );

  // ─────────────────────────────────────────────
  // INIT LOAD
  // ─────────────────────────────────────────────
  useEffect(() => {
    const token = getToken();
    if (token) fetchComplaintTypes();
  }, []);

  return (
    <ComplaintTypeContext.Provider
      value={{
        complaintTypes,
        activeComplaintTypes,
        loading,
        error,
        fetchComplaintTypes,
        createComplaintType,
        updateComplaintType,
        deleteComplaintType,
        updateComplaintTypeStatus
      }}
    >
      {children}
    </ComplaintTypeContext.Provider>
  );
};

// ─────────────────────────────────────────────
// HOOK
// ─────────────────────────────────────────────
export const useComplaintTypes = () =>
  useContext(ComplaintTypeContext);